/**
 * HonestLee Consumer Frontend v2 — Vite Entry Point
 *
 * This is the new Vite-powered entry point. The old CRA entry point (index.js)
 * stays untouched for backwards compatibility.
 *
 * Strategy: import the existing App.jsx so all old routes keep working.
 * New v2 components (.tsx) are built alongside old .jsx components and
 * swapped in one-by-one via route imports.
 */
import './i18n';

// 🔴 v70.046 — ONE chunk-recovery implementation, not two.
//
// Patrick, 2026-07-31, after a production deploy landed while he was using the site:
//   "prod is not healthy i hard refreshed same shit
//    TypeError: Failed to fetch dynamically imported module ... MergeAccountsPrompt-...js
//    Failed to load module script: ... MIME type of \"text/html\""
//
// THERE WERE TWO COMPETING RECOVERY PATHS, and the weaker one won:
//   1. this file — inline listeners with a SIXTY SECOND lockout (key hl_chunk_reload),
//      registered at module load so they fire FIRST and swallow the event;
//   2. utils/chunkRecovery.ts — the real implementation, used by ErrorBoundary, whose
//      installChunkErrorRecovery() was exported and NEVER CALLED ANYWHERE. Dead code.
//
// So a stale-chunk crash got exactly ONE reload and was then locked out for a full minute.
// Cloudflare Pages edge propagation was measured at 10-30s during this session, so that
// single retry raced the propagation, lost, and left the ErrorBoundary on screen. Hard
// refreshing did nothing because the lockout is stored in sessionStorage and survives it.
//
// Now there is one implementation, installed here, with bounded retries and backoff.
import { installChunkErrorRecovery, clearChunkRecoveryState } from './utils/chunkRecovery';

installChunkErrorRecovery();
// The app got this far, so any in-flight incident is over. Clearing the attempt counter means
// a LATER deploy gets a full retry budget instead of inheriting a spent one.
setTimeout(() => { try { clearChunkRecoveryState(); } catch { /* noop */ } }, 4000);

// EV-818 — when a freshly-deployed service worker takes control, reload ONCE so the new
// bundle (new languages, new UI) applies immediately — no more "I don't see it / incognito".
if ('serviceWorker' in navigator) {
  let _swReloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (_swReloading) return;
    // only auto-reload when WE caused the takeover after first load (avoid first-install loop)
    if (sessionStorage.getItem('hl_sw_ready') === '1') { _swReloading = true; window.location.reload(); }
  });
  navigator.serviceWorker.ready.then(() => { try { sessionStorage.setItem('hl_sw_ready', '1'); } catch { /* noop */ } });
}

// EV-603 P4 — referral capture: any landing URL carrying ?ref=<code> stores the
// code; once the user is (or gets) logged in, we claim it ONCE (server enforces
// one-acquisition-per-user) and the referrer earns their 100⭐ + downline.
try {
  const sp = new URLSearchParams(window.location.search);
  const refCode = sp.get('ref');
  if (refCode && !localStorage.getItem('hl_ref_claimed')) {
    localStorage.setItem('hl_pending_ref', refCode.toUpperCase());
  }
} catch { /* noop */ }
function hlClaimPendingReferral() {
  try {
    const pending = localStorage.getItem('hl_pending_ref');
    if (!pending || localStorage.getItem('hl_ref_claimed')) return;
    const tk = localStorage.getItem('venue_app_token') || localStorage.getItem('hl_auth_token') || localStorage.getItem('token');
    if (!tk) return;
    const base = (import.meta.env.VITE_API_URL || 'https://core.honestlee.app').replace(/\/$/, '');
    fetch(`${base}/api/me/referral-claim`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
      body: JSON.stringify({ code: pending }),
    }).then((r) => r.json()).then((j) => {
      if (j?.success) { localStorage.setItem('hl_ref_claimed', '1'); localStorage.removeItem('hl_pending_ref'); }
    }).catch(() => {});
  } catch { /* noop */ }
}
// EV-603/EV-625 — daily streak ping (once per device-day; server daily_cap=1
// makes it idempotent anyway) + one-time QR-acquisition claim after login
// (converts this device's anonymous venue-QR entries → venue signup credit).
function hlGamifyPings() {
  try {
    const tk = localStorage.getItem('venue_app_token') || localStorage.getItem('hl_auth_token') || localStorage.getItem('token');
    if (!tk) return;
    const base = (import.meta.env.VITE_API_URL || 'https://core.honestlee.app').replace(/\/$/, '');
    const hdrs = { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` };
    const day = new Date().toISOString().slice(0, 10);
    if (localStorage.getItem('hl_daily_open') !== day) {
      fetch(`${base}/api/me/daily-open`, { method: 'POST', headers: hdrs })
        .then((r) => r.json())
        .then((j) => { if (j?.success) localStorage.setItem('hl_daily_open', day); })
        .catch(() => {});
    }
    const anon = localStorage.getItem('hl_anon_id');
    if (anon && !localStorage.getItem('hl_qr_claimed')) {
      fetch(`${base}/api/me/qr-claim`, { method: 'POST', headers: hdrs, body: JSON.stringify({ anon_id: anon }) })
        .then((r) => r.json())
        .then((j) => { if (j?.success) localStorage.setItem('hl_qr_claimed', '1'); })
        .catch(() => {});
    }
  } catch { /* noop */ }
}
setTimeout(hlClaimPendingReferral, 4000);
setTimeout(hlGamifyPings, 5000);
document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') { hlClaimPendingReferral(); hlGamifyPings(); } });

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Toaster } from 'react-hot-toast';
import App from './App';
import { AuthProvider } from './contexts/AuthContext';
import './styles/globals-v2.css';
import './index.css';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

// ── CON-1606 — SELF-HEAL A STALE CHUNK ───────────────────────────────────────
// Patrick, on mobile, repeatedly:
//   TypeError: Failed to fetch dynamically imported module: .../MapPage-xxx.js
// Cloudflare Pages rotates asset hashes on every deploy. A device holding an
// old index.html (served by an OLD service worker that predates our
// network-first navigation logic) asks for a chunk that no longer exists, and
// the lazy route dies. Bumping CACHE_NAME fixes NEW visitors but cannot help a
// device whose installed worker never re-fetches — which is exactly why a
// hard refresh did not clear it for him.
//
// Vite fires `vite:preloadError` when a dynamic import 404s. Catch it, nuke
// every cache, drop the workers, and reload ONCE per session (the sessionStorage
// guard is load-bearing — without it a genuinely offline device reload-loops).
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  if (sessionStorage.getItem('hl_chunk_recovered')) return;   // already tried
  sessionStorage.setItem('hl_chunk_recovered', '1');
  const done = () => window.location.reload();
  Promise.all([
    caches?.keys?.().then((ks) => Promise.all(ks.map((k) => caches.delete(k)))).catch(() => {}),
    navigator.serviceWorker?.getRegistrations?.()
      .then((rs) => Promise.all(rs.map((r) => r.unregister()))).catch(() => {}),
  ]).then(done, done);
});

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID
  || process.env.REACT_APP_GOOGLE_CLIENT_ID
  || '';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 10 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <App />
          <Toaster
            position="top-center"
            toastOptions={{
              duration: 4000,
              className: 'bg-amber-500 text-white',
            }}
          />
        </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </GoogleOAuthProvider>
);

// Patrick rule 2026-04-26: register the service worker so PWA install +
// offline caching actually work. Vite migration months ago missed this —
// only the old CRA index.js called register(). Result: SW file deployed
// but never installed on clients → no PWA install prompt on Android Chrome,
// no offline pre-cache pack, no runtime cache. Now fixed.
serviceWorkerRegistration.register({
  onUpdate: (registration) => {
    console.log('[SW] New content available, will refresh after reload');
    if (registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  },
  onSuccess: () => {
    console.log('[SW] Service worker registered + caching for offline use');
  },
});

// ── EV-156 Web Push — subscribe so the server can send tray notifications even
// when the PWA is closed/backgrounded/locked. Only subscribes once the user has
// granted notification permission (e.g. via the proximity card's "Notify me").
const HL_VAPID_PUBLIC_KEY = 'BBLZQKKhGA10GCpLGv6c4Iy7zoa9Sjs-hoTzvYc0rCpfYxMr-KH9eFAaLE4LTcq6uhuN4_Lsx9FQZ_-YcPnv7so'; // EV-211 part 2: regenerated (old private key lost)
const HL_BACKEND = (import.meta.env && import.meta.env.VITE_BACKEND_URL) || 'https://core.honestlee.app';
function hlUrlB64ToUint8(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}
let hlPushDone = false;
// Decode the logged-in user's id from the JWT so push subscriptions can be
// TARGETED to a specific user (test pings must only reach the intended person,
// never every visitor who happens to have granted notifications).
function hlCurrentUserId() {
  try {
    const t = localStorage.getItem('venue_app_token') || localStorage.getItem('token');
    if (!t) return null;
    const payload = JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.userId || payload.id || payload.sub || null;
  } catch { return null; }
}
async function hlSetupPush() {
  if (hlPushDone) return;
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    const reg = await navigator.serviceWorker.ready;
    // Reuse the existing subscription if present — do NOT re-mint on every open
    // (re-minting retired the live sub and dropped in-flight pings). The SW's
    // pushsubscriptionchange handler self-heals genuine invalidation.
    //
    // EV-211 part 2: the VAPID public key CHANGED (old private key was lost).
    // A PushSubscription minted with the OLD applicationServerKey can never be
    // delivered to with the NEW key — and calling subscribe() with a different
    // key while a stale sub exists throws. So: if an existing sub was minted
    // with a different key, unsubscribe it FIRST, then re-mint with the new key.
    const wantKey = hlUrlB64ToUint8(HL_VAPID_PUBLIC_KEY);
    let sub = await reg.pushManager.getSubscription();
    if (sub) {
      const cur = sub.options && sub.options.applicationServerKey;
      const curBytes = cur ? new Uint8Array(cur) : null;
      const sameKey =
        curBytes &&
        curBytes.length === wantKey.length &&
        curBytes.every((b, i) => b === wantKey[i]);
      if (!sameKey) {
        try { await sub.unsubscribe(); } catch (e) { /* best effort */ }
        sub = null;
      }
    }
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: wantKey,
      });
    }
    const body = sub.toJSON();
    body.user_id = hlCurrentUserId(); // tag for targeted delivery
    const res = await fetch(HL_BACKEND + '/api/push/subscribe', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    if (res.ok) { hlPushDone = true; console.log('[Push] subscribed', body.user_id || '(anon)'); }
  } catch (e) { console.warn('[Push] subscribe failed', e); }
}
setTimeout(hlSetupPush, 3000);
// Re-try once when the tab regains focus (user may have just granted permission).
document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') hlSetupPush(); });
